import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ManagerRoute from './components/ManagerRoute';
import Header from './layout/header';
import Sidebar from './layout/sidebar';
import Login from './views/Login';
import Dashboard from './pages/dashboard/Dashboard';
import AllLateLoans from './pages/AllLateLoans';
import UnassignedLoans from './pages/UnassignedLoans';
import AssignedLoans from './pages/AssignedLoans';
import ManageTeam from './pages/ManageTeam';
import LoanDetails from './pages/LoanDetails';
import Promises from './pages/Promises';
import ArrearsAging from './pages/ArrearsAging';
import AgentPerformance from './pages/AgentPerformance';

// Component to redirect based on user role
function RoleBasedRedirect() {
  const { isManager, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  // Managers go to dashboard, agents go to assigned loans
  return <Navigate to={isManager ? "/dashboard" : "/assigned-loans"} replace />;
}

function AppContent() {
  const [showSideBar, setShowSideBar] = useState(true);
  const location = useLocation();

  // Don't show header/sidebar on login page
  const isLoginPage = location.pathname === '/login';

  if (isLoginPage) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  }

  return (
    <ProtectedRoute>
      <div className="">
        <Header showSideBar={showSideBar} onToggleSideBar={(show) => setShowSideBar(!show)} />
        <div className='flex min-h-screen'>
          {showSideBar ? (
            <div className={`w-1/5 min-h-screen`}>
              <Sidebar currentPath={location.pathname} />
            </div>
          ) : null}
          <div className={`${showSideBar ? 'w-4/5 border-l' : 'w-full'} bg-gray-50`}>
            <Routes>
              {/* Root route - redirect based on role */}
              <Route path="/" element={<RoleBasedRedirect />} />

              {/* Dashboard - Manager only */}
              <Route path="/dashboard" element={<ManagerRoute><Dashboard /></ManagerRoute>} />

              {/* Manager-only routes */}
              <Route path="/all-late-loans" element={<ManagerRoute><AllLateLoans /></ManagerRoute>} />
              <Route path="/unassigned-loans" element={<ManagerRoute><UnassignedLoans /></ManagerRoute>} />
              <Route path="/manage-team" element={<ManagerRoute><ManageTeam /></ManagerRoute>} />

              {/* Shared routes (both manager and agent) */}
              <Route path="/assigned-loans" element={<AssignedLoans />} />
              <Route path="/loan/:loanId" element={<LoanDetails />} />
              <Route path="/promises" element={<Promises />} />
              <Route path="/reports/arrears-aging" element={<ArrearsAging />} />
              <Route path="/reports/agent-performance" element={<AgentPerformance />} />
            </Routes>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastContainer
          position="top-right"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
